import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { TennisBall, Eye, EyeSlash, Ticket } from '@phosphor-icons/react';
import { captureInviteCode } from '../lib/inviteCode';

export default function SignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    setInviteCode(captureInviteCode(location.search));
  }, [location.search]);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authAPI.signup(name, email, password);
      login(res.data.user, res.data.player, res.data.has_ladder_access, res.data.profile_completed);
      // New user — no player record yet, send to invite redemption (preserve code if present)
      const target = inviteCode ? `/redeem-invite?code=${inviteCode}` : '/redeem-invite';
      navigate(target, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-[#fbfaf8]">
      <div className="absolute inset-0 h-[35vh]">
        <img
          src="https://images.unsplash.com/photo-1719760825481-90f57e905ccb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHx0ZW5uaXMlMjBjb3VydCUyMGdyYXNzfGVufDB8fHx8MTc3NjIwMzAzOHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-[#fbfaf8]" />
      </div>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-end pb-12 px-6"
           style={{ minHeight: '100vh' }}>
        <div className="mb-auto pt-12 text-center">
          <div className="inline-flex items-center gap-2">
            <TennisBall size={28} weight="duotone" className="text-white" />
            <span className="font-serif text-2xl text-white tracking-tight">The Rippers Club</span>
          </div>
        </div>

        <div className="w-full max-w-sm bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] shadow-xl p-6 space-y-5 animate-fade-in-up">
          <div className="text-center">
            <h1 className="font-serif text-3xl text-[#013523] tracking-tight">Create Account</h1>
            <p className="text-sm text-[#6a766e] mt-1">
              {inviteCode ? 'Sign up to redeem your invite' : 'Sign up, then enter your invite code'}
            </p>
          </div>

          {inviteCode && (
            <div
              data-testid="invite-code-pill"
              className="flex items-center gap-2 bg-[#015e3f]/10 text-[#015e3f] text-xs rounded-full px-3 py-2 justify-center"
            >
              <Ticket size={14} weight="duotone" />
              <span>Invite code applied: </span>
              <span className="font-mono font-semibold tracking-wider">{inviteCode}</span>
            </div>
          )}

          {error && (
            <div data-testid="signup-error" className="bg-red-50 text-red-600 text-sm rounded-xl px-4 py-3 border border-red-200">
              {error}
            </div>
          )}

          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label className="text-xs font-sans tracking-[0.1em] uppercase text-[#6a766e] mb-1.5 block">Full Name</label>
              <input
                data-testid="signup-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-white border border-[#e3dcd1] rounded-xl px-4 py-3 text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f] transition-all"
                placeholder="John Smith"
                required
              />
            </div>
            <div>
              <label className="text-xs font-sans tracking-[0.1em] uppercase text-[#6a766e] mb-1.5 block">Email</label>
              <input
                data-testid="signup-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-[#e3dcd1] rounded-xl px-4 py-3 text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f] transition-all"
                placeholder="your@email.com"
                required
              />
            </div>
            <div>
              <label className="text-xs font-sans tracking-[0.1em] uppercase text-[#6a766e] mb-1.5 block">Password</label>
              <div className="relative">
                <input
                  data-testid="signup-password-input"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-[#e3dcd1] rounded-xl px-4 py-3 pr-12 text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f] transition-all"
                  placeholder="Create a password"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6a766e] hover:text-[#013523]"
                >
                  {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <button
              data-testid="signup-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#015e3f] text-white rounded-full px-6 py-3 font-medium transition-transform active:scale-95 hover:bg-[#014d34] disabled:opacity-50"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-sm text-[#6a766e]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#015e3f] font-medium hover:underline" data-testid="go-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
